import { Link } from '@inertiajs/react'
import { ChevronRight } from 'lucide-react'
import { Avatar } from '@/components/design-system'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import type { NavigableWorker } from '@/frontend/types/inertia'
import ProjectAgentName from '@/frontend/components/DigitalWorkers/ProjectAgentName'
import { workerNavigationTarget } from './selectWorker'
import { groupTarget, visibleGroupPages, visibleGroups } from './workerGroups'

interface AgentSectionLinksProps {
  worker: NavigableWorker
  currentUrl: string
  active: boolean
  businessProcessesEnabled: boolean
  onNavigate: () => void
}

function AgentSectionLinks({
  worker,
  currentUrl,
  active,
  businessProcessesEnabled,
  onNavigate,
}: AgentSectionLinksProps) {
  const [path, query] = currentUrl.split('?')
  const params = new URLSearchParams(query ?? '')
  const ready = worker.provisioning_status === 'ready'
  const provisioning = worker.provisioning_status === 'pending' || worker.provisioning_status === 'provisioning'
  const onboardingSuffix = active && params.get('onboarding') === '1' ? '?onboarding=1' : ''
  const withOnboarding = (href: string) => (href.includes('?') ? href : `${href}${onboardingSuffix}`)

  return (
    <nav aria-label={`Seções de ${worker.name}`} className="ml-4 space-y-0.5 pb-1 pt-0.5">
      {/* visibleGroups já descarta o grupo Processos quando a flag está
          desligada — é a única página dele, então o item inteiro some. */}
      {visibleGroups(businessProcessesEnabled).map((group) => {
        const Icon = group.icon
        const pages = visibleGroupPages(group, businessProcessesEnabled)
        const groupReady = group.key === 'chat' ? ready && worker.probe_connect_ready : ready
        const target = groupTarget(pages, groupReady)
        const destination = target ?? pages[0] ?? group.pages[0]
        const gatedOff = target === null
        const groupActive = active && group.pages.some((page) => page.isActive(path, worker.id, params))

        return (
          <Link
            key={group.key}
            href={withOnboarding(destination.href(worker.id))}
            onClick={onNavigate}
            aria-current={groupActive ? 'location' : undefined}
            data-testid={`wfn-agent-group-${worker.id}-${group.key}`}
            className={cn(
              'flex min-h-7 items-center gap-2 rounded-md px-2 py-1 text-[12.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              groupActive
                ? 'bg-agent-soft text-foreground'
                : group.key === 'chat'
                  ? 'text-agent hover:bg-accent hover:text-agent'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              gatedOff && 'opacity-60',
            )}
          >
            <Icon aria-hidden="true" className="size-3.5 flex-none" strokeWidth={1.75} />
            <span className="min-w-0 flex-1 truncate">{group.label}</span>
            {group.key === 'processos' && worker.business_process_count > 0 && (
              <span
                data-testid={`wfn-processos-badge-${worker.id}`}
                className="rounded-full bg-agent-soft px-1.5 text-[10.5px] font-bold leading-[18px] text-agent"
              >
                {worker.business_process_count}
              </span>
            )}
            {gatedOff && (
              <>
                <span
                  aria-hidden="true"
                  className={cn(
                    'size-1.5 flex-none rounded-full',
                    provisioning ? 'animate-live-pulse bg-warning' : 'bg-faint-foreground',
                  )}
                />
                <span className="sr-only">
                  {provisioning ? '(instância em preparação)' : '(instância indisponível)'}
                </span>
              </>
            )}
          </Link>
        )
      })}
    </nav>
  )
}

interface Props {
  worker: NavigableWorker
  currentUrl: string
  active: boolean
  open: boolean
  businessProcessesEnabled: boolean
  onOpenChange: (open: boolean) => void
  onNavigate: () => void
}

function agentStatus(worker: NavigableWorker): { avatar: 'online' | 'away' | 'busy'; label: string } {
  if (worker.provisioning_status === 'ready') return { avatar: 'online', label: 'Ativa' }
  if (worker.provisioning_status === 'failed') return { avatar: 'busy', label: 'Falha' }
  return { avatar: 'away', label: 'Em criação' }
}

export default function AgentDisclosure({
  worker,
  currentUrl,
  active,
  open,
  businessProcessesEnabled,
  onOpenChange,
  onNavigate,
}: Props) {
  const status = agentStatus(worker)
  const target = workerNavigationTarget(worker, currentUrl)

  return (
    <Collapsible
      open={open}
      onOpenChange={onOpenChange}
      data-testid={`wfn-agent-row-${worker.id}`}
      style={agentHueStyle(worker.id)}
    >
      <div
        data-active={active || undefined}
        className={cn(
          'group flex min-w-0 items-center rounded-[9px] text-[13px] transition-colors duration-150',
          active && !open
            ? 'bg-accent text-foreground'
            : open
              ? 'text-foreground hover:bg-accent/60'
              : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
        )}
      >
        <Link
          href={target.href}
          preserveState={target.preserveState}
          preserveScroll={target.preserveScroll}
          onClick={onNavigate}
          aria-label={`${worker.name}, ${status.label}`}
          data-testid={`wfn-agent-portal-${worker.id}`}
          title={worker.name}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-[9px] px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <Avatar
            src={worker.avatar_url ?? undefined}
            alt=""
            name={worker.name}
            hueSeed={worker.id}
            size="xxs"
            status={status.avatar}
            className="flex-none"
          />
          <ProjectAgentName workerId={worker.id} name={worker.name} className="flex-1" nameClassName="font-semibold" />
        </Link>

        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-label={`${open ? 'Ocultar' : 'Mostrar'} seções de ${worker.name}`}
            data-testid={`wfn-agent-disclosure-${worker.id}`}
            className="mr-1 flex size-7 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight
              aria-hidden="true"
              className={cn('size-3.5 transition-transform duration-150 motion-reduce:transition-none', open && 'rotate-90')}
              strokeWidth={1.75}
            />
          </button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent className="overflow-hidden data-[state=closed]:[animation:collapse_150ms_ease-in] data-[state=open]:[animation:expand_150ms_ease-out] motion-reduce:[animation:none]">
        <AgentSectionLinks
          worker={worker}
          currentUrl={currentUrl}
          active={active}
          businessProcessesEnabled={businessProcessesEnabled}
          onNavigate={onNavigate}
        />
      </CollapsibleContent>
    </Collapsible>
  )
}
